/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react/no-unknown-property */
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Action,
  ActionIconDropdown,
  Card,
  DetailItemList,
  Dropdown,
  FontWeight, HStack, ICON_CASH_CHECK, Message, SimpleTable, Stack, Text,
  TextSkeleton,
  TypographyName,
  UserPreview,
  UserPreviewSkeleton,
} from '@doveit/bricks';
import { formatDate, formatEuro, formatPercentage } from '@doveit/hammer';
import { raise } from '../../../utils';
import RightColumnPageLayout from '../../../layouts/right-column-page-layout/RightColumnPageLayout';
import InvoiceAvatar from '../../components/invoice-avatar/InvoiceAvatar';
import { setVerticalEllipsis } from '../../../utils/styles/verticalEllipsis.style';
import CopyLinkAction from '../../../components/copy-link-action/CopyLinkAction';
import useCurrentUrl from '../../../hooks/use-current-url/useCurrentUrl';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import useInvoice from '../../hooks/use-invoice/useInvoice';
import GenericErrorPage from '../../../pages/errors/generic/GenericErrorPage';
import RightColumnPageSkeleton from '../../../components/page-skeleton/RightColumnPageSkeleton';
import useContact from '../../../contact/hooks/use-contact/useContact';
import { invoiceTypeLabels, paymentMethodWizardLabels } from '../../../labels';
import useAssignmentById from '../../../assignment/hooks/use-assignment-by-id/useAssignmentById';
import useProperty from '../../../property/hooks/use-property/useProperty';
import { useAgent } from '../../../hooks/use-agent/useAgent';
import ViewAgentPopoverPreview from '../../../agent/containers/view-agent-popover-preview/ViewAgentPopoverPreview';
import { ViewAgentSmallPreview } from '../../../agent/components';
import { toAgentPreviewProps } from '../../../agent/mappers/toAgentPreviewProps';
import ViewPropertyPreview from '../../../property/containers/view-property-preview/ViewPropertyPreview';
import { InvoiceStatus, ReferenceType } from '../../../domain/types';
import { Invoice } from '../../../providers/api/dtos';
import ViewContactPopoverPreview from '../../../contact/containers/view-contact-popover-preview/ViewContactPopoverPreview';
import { NO_VALUE_SYMBOL } from '../../../property/utils';
import { goToDetailPage } from '../../../utils/navigate/utils';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import UpsertInvoiceAction from '../../containers/upsert-invoice-action/UpsertInvoiceAction';
import UpdateInvoiceStatus from '../../containers/update-invoice-status/UpdateInvoiceStatus';
import { useCurrentAgentIsSameAgentOrManager } from '../../../agent/hooks/use-current-agent-is-same-agent-or-manager/useCurrentAgentIsSameAgentOrManager';
import ManageInvoiceCommissionsAction from '../../containers/manage-invoice-commissions-action/ManageInvoiceCommissionsAction';
import useCompany from '../../../company/hooks/use-company/useCompany';
import ViewCompanyPopoverPreview from '../../../company/containers/view-company-popover-preview/ViewCompanyPopoverPreview';
import CompanyAvatar from '../../../company/components/company-avatar/CompanyAvatar';
import { computeGrossValue } from '../../utils';
import InvoiceCommissionTableRow from '../../components/invoice-commission-table-row/InvoiceCommissionTableRow';
import InvoiceCommissionCard from '../../components/invoice-commission-card/InvoiceCommissionCard';
import InvoiceValuePopover from '../../components/invoice-value-popover/InvoiceValuePopover';
import { useInvoiceCommissionsByRole } from '../../hooks/use-invoice-commissions-by-role/useInvoiceCommissionsByRole';
import DeleteInvoiceAction from '../../containers/delete-invoice-action/DeleteInvoiceAction';
import ViewEntityPreviewSkeleton from '../../../components/view-entity-preview-skeleton/ViewEntityPreviewSkeleton';

export const LOAD_INVOICE_ERROR_MESSAGE = 'Non è stato possibile caricare le informazioni del pagamento';
export const LOAD_PROPERTY_ERROR_MESSAGE = 'Errore immobile';
export const LOAD_COMMISSIONS_ERROR_MESSAGE = 'Errore commissioni';
export const LOAD_CONTACT_ERROR_MESSAGE = 'Errore contatto';
export const LOAD_COMPANY_ERROR_MESSAGE = 'Errore persona giuridica';
export const LOAD_AGENT_ERROR_MESSAGE = 'Errore agente';

const ViewInvoicePage: React.FC = () => {
  const isMobile = useIsDevice('mobile');
  const { userIsAdmin, userIsFinance } = useRBAC();
  const navigate = useNavigate();

  const { id } = useParams();
  const invoiceId = id
    ? parseInt(id, 10)
    : raise('missing invoice id');

  const currentUrl = useCurrentUrl();

  const {
    data: invoice,
    isLoading: isInvoiceLoading,
    error: invoiceError,
    mutate: mutateInvoice,
  } = useInvoice(invoiceId);

  const { data: assignment } = useAssignmentById(invoice?.assignmentId);

  const {
    data: contact,
    isLoading: isContactLoading,
    error: contactError,
  } = useContact(invoice?.reference.type === ReferenceType.CONTACT ? invoice.reference.id : undefined);

  const {
    data: company,
    isLoading: isCompanyLoading,
    error: companyError,
  } = useCompany(invoice?.reference.type === ReferenceType.COMPANY ? invoice.reference.id : undefined);

  const {
    data: property,
    isLoading: isPropertyLoading,
    error: propertyError,
  } = useProperty(assignment?.propertyId);

  const {
    data: agent,
    isLoading: isAgentLoading,
    error: agentError,
  } = useAgent(assignment?.agentId);

  const isPropertyAgentOrManager = useCurrentAgentIsSameAgentOrManager(assignment?.agentId);

  const grossValue = React.useMemo(() => invoice && computeGrossValue(invoice.value, invoice.vat), [invoice]);

  const {
    data: filteredCommissions = [],
    authorized: filteredCommissionsAuthorized,
  } = useInvoiceCommissionsByRole(invoice || {} as Invoice);

  if (invoiceError) {
    return <GenericErrorPage title={LOAD_INVOICE_ERROR_MESSAGE} />;
  }

  if (!invoice || isInvoiceLoading) {
    return <RightColumnPageSkeleton />;
  }

  return (
    <RightColumnPageLayout
      aria-label="Pagina dettaglio pagamento"
      title={(
        <HStack
          data-testid="page-title"
          gap={200}
          wrap="nowrap"
          alignItems="center"
        >
          <InvoiceAvatar size="M" />

          <Stack gap={0}>
            {isContactLoading && (
              <TextSkeleton
                width="10rem"
                aria-label="Informazioni contatto in caricamento"
                typography={TypographyName.HEADING_4}
              />
            )}

            {contactError && (
              <Text.H4
                fontWeight={FontWeight.REGULAR}
                css={setVerticalEllipsis()}
                color="critical.default.high"
              >
                {LOAD_CONTACT_ERROR_MESSAGE}
              </Text.H4>
            )}

            {contact && (
              <Text.H4
                fontWeight={FontWeight.REGULAR}
                css={setVerticalEllipsis()}
              >
                {contact.name}
              </Text.H4>
            )}

            {isCompanyLoading && (
              <TextSkeleton
                width="10rem"
                aria-label="Informazioni persona giuridica in caricamento"
                typography={TypographyName.HEADING_4}
              />
            )}

            {companyError && (
              <Text.H4
                fontWeight={FontWeight.REGULAR}
                css={setVerticalEllipsis()}
                color="critical.default.high"
              >
                {LOAD_COMPANY_ERROR_MESSAGE}
              </Text.H4>
            )}

            {company && (
              <Text.H4
                fontWeight={FontWeight.REGULAR}
                css={setVerticalEllipsis()}
              >
                {company.name}
              </Text.H4>
            )}

            <HStack>
              <Text.BodySmall>
                {invoiceTypeLabels[invoice.type]}
              </Text.BodySmall>

              {isPropertyLoading && (
                <TextSkeleton
                  width="5rem"
                  aria-label="Informazioni immobile in caricamento"
                  typography={TypographyName.BODY_SMALL}
                />
              )}

              {propertyError && (
                <Text.BodySmall color="critical.default.high">
                  {LOAD_PROPERTY_ERROR_MESSAGE}
                </Text.BodySmall>
              )}

              {property && (
                <Text.BodySmall>
                  {property.referenceId}
                </Text.BodySmall>
              )}
            </HStack>
          </Stack>
        </HStack>
      )}
      actions={(
        <ActionIconDropdown
          label="Azioni sul pagamento"
          aria-label="Azioni sul pagamento"
        >
          <CopyLinkAction link={currentUrl}>
            {({ onCopy }) => (
              <Dropdown.Option
                label="Copia riferimento"
                aria-label="Copia riferimento al pagamento"
                onClick={onCopy}
              />
            )}
          </CopyLinkAction>
          {(userIsAdmin || userIsFinance) && [InvoiceStatus.DRAFT, InvoiceStatus.PROFORMA].includes(invoice.status) && (
            <DeleteInvoiceAction
              invoiceId={invoiceId}
              onSuccess={() => navigate('/invoices/explore')}
            >
              {({ onOpenModal }) => (
                <Dropdown.Option
                  label="Elimina pagamento"
                  aria-label="Elimina pagamento"
                  onClick={onOpenModal}
                />
              )}
            </DeleteInvoiceAction>
          )}
        </ActionIconDropdown>
      )}
      sidePrimaryContent={(
        <Card paddingSize="S">
          <Card.Header isSecondary>
            <UpdateInvoiceStatus
              invoice={invoice}
              showAsBadge={isPropertyAgentOrManager}
              emphasis="high"
              showIcon
              onSuccess={mutateInvoice}
            />
          </Card.Header>

          <Card.Content>
            <DetailItemList>
              {invoice.createdAt && (
                <DetailItemList.Item
                  aria-label="Data creazione"
                  label="Data creazione"
                >
                  {formatDate(new Date(invoice.createdAt))}
                </DetailItemList.Item>
              )}

              <DetailItemList.Item
                aria-label="Agente"
                label="Agente"
                fullWidth
              >
                {isAgentLoading && (
                  <UserPreviewSkeleton
                    size="S"
                    aria-label="Informazioni agente in caricamento"
                  />
                )}

                {agentError && (
                  <UserPreview
                    size="S"
                    name={LOAD_AGENT_ERROR_MESSAGE}
                  />
                )}

                {agent && (
                  <ViewAgentPopoverPreview
                    agent={agent}
                    trigger={(
                      <ViewAgentSmallPreview
                        {...toAgentPreviewProps(agent)}
                        size="S"
                      />
                    )}
                  />
                )}
              </DetailItemList.Item>
            </DetailItemList>
          </Card.Content>
        </Card>
      )}
    >
      <Card aria-label="Dettagli pagamento">
        <Card.Header
          icon={{ path: ICON_CASH_CHECK }}
          title="Dettagli"
          aria-label="Header pagamento"
          primaryActions={!isMobile ? [
            (userIsAdmin || userIsFinance) && (
              <UpsertInvoiceAction
                invoice={invoice}
                onSuccess={() => mutateInvoice()}
              >
                {({ openUpsertInvoiceModal, disabled }) => (
                  <Action
                    label="Modifica"
                    aria-label="Modifica pagamento"
                    onClick={openUpsertInvoiceModal}
                    disabled={disabled}
                    size="S"
                  />
                )}
              </UpsertInvoiceAction>
            ),
          ] : undefined}
          secondaryActions={!isMobile
            ? undefined : [
              (userIsAdmin || userIsFinance) && (
                <UpsertInvoiceAction
                  invoice={invoice}
                  onSuccess={() => mutateInvoice()}
                >
                  {({ openUpsertInvoiceModal, disabled }) => (
                    <Dropdown.Option
                      label="Modifica"
                      aria-label="Modifica pagamento"
                      onClick={openUpsertInvoiceModal}
                      disabled={disabled}
                    />
                  )}
                </UpsertInvoiceAction>
              ),
            ]}
        />

        <Card.Content>
          <Stack gap={300}>
            <Stack gap={100}>
              <Text.Caption>
                Riferimento
              </Text.Caption>

              {isPropertyLoading && (
                <ViewEntityPreviewSkeleton
                  aria-label="Informazioni immobile in caricamento"
                />
              )}

              {propertyError && (
                <Message
                  message={LOAD_PROPERTY_ERROR_MESSAGE}
                  type="critical"
                />
              )}

              {property && (
                <ViewPropertyPreview
                  property={property}
                  onClick={({ id: propertyId }) => goToDetailPage(`${propertyId}`, ReferenceType.PROPERTY)}
                />
              )}
            </Stack>

            <DetailItemList columns={2}>
              <DetailItemList.Item label="Tipologia">
                {invoiceTypeLabels[invoice.type]}
              </DetailItemList.Item>

              <DetailItemList.Item label={[InvoiceStatus.PAID, InvoiceStatus.COLLECTED, InvoiceStatus.NOT_PAID].includes(invoice.status) ? 'Data chiusura' : 'Data presunta'}>
                {formatDate(new Date(invoice.paymentDate))}
              </DetailItemList.Item>

              {invoice.status === InvoiceStatus.PROFORMA && (
                <DetailItemList.Item label="Rif. Proforma">
                  {invoice.proformaInvoiceId}
                </DetailItemList.Item>
              )}

              {[InvoiceStatus.PAID, InvoiceStatus.COLLECTED].includes(invoice.status) && (
                <DetailItemList.Item label="Rif. Fattura">
                  {invoice.invoiceId}
                </DetailItemList.Item>
              )}

              {grossValue !== undefined && (
                <DetailItemList.Item label="Importo">
                  <InvoiceValuePopover
                    invoice={invoice}
                    hideCommissions
                    trigger={(
                      <HStack>
                        {formatEuro(grossValue)}
                        <Text.Mini fontWeight={FontWeight.LIGHT} whitespace="nowrap">
                          ({formatPercentage(invoice.vat!!)} IVA)
                        </Text.Mini>
                      </HStack>
                    )}
                  />
                </DetailItemList.Item>
              )}

              {invoice.vat === undefined && (
                <DetailItemList.Item label="Importo (netto)">
                  {formatEuro(invoice.value)}
                </DetailItemList.Item>
              )}

              {invoice.paymentMethod && (
                <DetailItemList.Item label="Metodo">
                  {paymentMethodWizardLabels[invoice.paymentMethod]}
                </DetailItemList.Item>
              )}

              <DetailItemList.Item label="Intestatario">
                {isContactLoading && (
                  <UserPreviewSkeleton
                    size="S"
                    aria-label="Informazioni contatto in caricamento"
                  />
                )}

                {isCompanyLoading && (
                  <UserPreviewSkeleton
                    size="S"
                    aria-label="Informazioni persona giuridica in caricamento"
                  />
                )}

                {contactError && (
                  <UserPreview
                    size="S"
                    name={LOAD_CONTACT_ERROR_MESSAGE}
                  />
                )}

                {companyError && (
                  <HStack gap={100} wrap="nowrap">
                    <CompanyAvatar size="S" />
                    <Text.BodySmall
                      fontWeight={FontWeight.MEDIUM}
                      css={setVerticalEllipsis()}
                      color="critical.default.high"
                    >
                      {LOAD_COMPANY_ERROR_MESSAGE}
                    </Text.BodySmall>
                  </HStack>
                )}

                {contact && (
                  <ViewContactPopoverPreview
                    contact={contact}
                    trigger={(
                      <UserPreview
                        name={contact.name || NO_VALUE_SYMBOL}
                        size="S"
                      />
                    )}
                  />
                )}

                {company && (
                  <ViewCompanyPopoverPreview
                    company={company}
                    trigger={(
                      <HStack gap={100} wrap="nowrap">
                        <CompanyAvatar size="S" />
                        <Text.BodySmall
                          fontWeight={FontWeight.MEDIUM}
                          css={setVerticalEllipsis()}
                        >
                          {company.name}
                        </Text.BodySmall>
                      </HStack>
                    )}
                  />
                )}
              </DetailItemList.Item>
            </DetailItemList>

            {(invoice.notes?.length || 0) > 0 && (
              <Stack gap={100}>
                <Text.Caption>
                  Note
                </Text.Caption>

                <Text.Body>
                  {invoice.notes![0].text}
                </Text.Body>
              </Stack>
            )}
          </Stack>
        </Card.Content>

        {filteredCommissionsAuthorized && (
          <>
            <Card.Divider />

            <Card.Header
              title="Commissioni"
              aria-label="Header commissioni"
              primaryActions={[
                (!isMobile && (userIsAdmin || userIsFinance)) && (
                  <ManageInvoiceCommissionsAction
                    aria-label="Azione per gestire le commissioni relative al pagamento"
                    invoice={invoice}
                    onSuccess={() => mutateInvoice()}
                  />
                ),
              ]}
              secondaryActions={[
                (isMobile && (userIsAdmin || userIsFinance)) && (
                  <ManageInvoiceCommissionsAction
                    invoice={invoice}
                    onSuccess={() => mutateInvoice()}
                  >
                    {({ manageCommissions }) => (
                      <Dropdown.Option
                        label="Gestisci"
                        aria-label="Azione per gestire le commissioni relative al pagamento"
                        onClick={manageCommissions}
                      />
                    )}
                  </ManageInvoiceCommissionsAction>
                ),
              ]}
            />

            <Card.Content>

              {filteredCommissions.length === 0 && (
                <Message
                  message="Non sono presenti commissioni"
                />
              )}

              {filteredCommissions.length > 0 && (
                <>
                  {!isMobile && (
                    <SimpleTable>
                      <SimpleTable.Body>
                        {filteredCommissions.map((commission) => (
                          <SimpleTable.Row key={`invoice-commission-${commission.agentId}`} asChild>
                            <InvoiceCommissionTableRow commission={commission} />
                          </SimpleTable.Row>
                        ))}
                      </SimpleTable.Body>
                    </SimpleTable>
                  )}
                  {isMobile && (
                    <Stack gap={100}>
                      {filteredCommissions.map((commission, i) => (
                        <InvoiceCommissionCard
                          key={`invoice-commission-${commission.agentId}`}
                          title={`Commissione (${i + 1})`}
                          commission={commission}
                        />
                      ))}
                    </Stack>
                  )}
                </>
              )}
            </Card.Content>
          </>
        )}

      </Card>
    </RightColumnPageLayout>
  );
};

export default ViewInvoicePage;
